/**
 * Configuration Getter Utilities
 * Provides a unified interface for accessing all configuration
 */

import type { Environment, FeatureFlags, CountryConfig } from '@schoolidb/types';

import { getApiConfig, buildApiUrl, API_PATHS, type ApiConfig } from './api';
import { getComponentConfig, type ComponentConfig } from './components';
import {
  getCountryConfig,
  isCountrySupported,
  getSupportedCountryCodes,
  getCountryOptions,
  getSchoolTypes,
  getCurriculumTypes,
  getCurrencyConfig,
  getAddressFormat,
  getDateFormat,
  getPhoneFormat,
  getTimezone,
  DEFAULT_COUNTRY_CODE,
} from './countries';
import {
  getEnvironment,
  getEnvironmentConfig,
  isDevelopment,
  isProduction,
  isStaging,
  isDebugEnabled,
  type EnvironmentConfig,
} from './environments';
import { getFeatureFlags, isFeatureEnabled, createFeatureChecker } from './features';
import {
  getGoogleConfig,
  getGoogleMapsConfig,
  getGoogleOAuthConfig,
  getGooglePlacesConfig,
  getMapStyles,
  type GoogleConfig,
} from './google';

/**
 * Complete application configuration
 */
export interface AppConfig {
  environment: EnvironmentConfig;
  api: ApiConfig;
  features: FeatureFlags;
  country: CountryConfig;
  google: GoogleConfig;
  components: ComponentConfig;
}

/**
 * Get complete application configuration
 * @param countryCode - Country code for country-specific config (defaults to IN)
 */
export const getConfig = (countryCode: string = DEFAULT_COUNTRY_CODE): AppConfig => ({
    environment: getEnvironmentConfig(),
    api: getApiConfig(),
    features: getFeatureFlags(),
    country: getCountryConfig(countryCode),
    google: getGoogleConfig(countryCode),
    components: getComponentConfig(),
  });


/**
 * Configuration context for React applications
 * Can be used to create a ConfigProvider
 */
export interface ConfigContextValue {
  /** Current environment */
  environment: Environment;
  /** Current country code */
  countryCode: string;
  /** Check if a feature is enabled */
  isFeatureEnabled: (feature: keyof FeatureFlags) => boolean;
  /** Get country-specific configuration */
  getCountryConfig: () => CountryConfig;
  /** Check if running in development */
  isDevelopment: boolean;
  /** Check if running in production */
  isProduction: boolean;
  /** Check if debug mode is enabled */
  isDebugEnabled: boolean;
}

/**
 * Create configuration context value
 * @param countryCode - Current country code
 */
export const createConfigContext = (countryCode: string): ConfigContextValue => ({
    environment: getEnvironment(),
    countryCode,
    isFeatureEnabled: createFeatureChecker(),
    getCountryConfig: () => getCountryConfig(countryCode),
    isDevelopment: isDevelopment(),
    isProduction: isProduction(),
    isDebugEnabled: isDebugEnabled(),
  });

/**
 * Platform detection utilities
 */
export const platform = {
  /** Check if running in browser */
  isBrowser: typeof window !== 'undefined',
  /** Check if running in Node.js */
  isNode: typeof process !== 'undefined' && process.versions?.node,
  /** Check if running in React Native */
  isReactNative:
    typeof navigator !== 'undefined' && navigator.product === 'ReactNative',
  /** Check if running on iOS */
  isIOS:
    typeof navigator !== 'undefined' &&
    /iPad|iPhone|iPod/.test(navigator.userAgent),
  /** Check if running on Android */
  isAndroid:
    typeof navigator !== 'undefined' && /Android/.test(navigator.userAgent),
  /** Check if running on mobile */
  isMobile:
    typeof navigator !== 'undefined' &&
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    ),
} as const;

/**
 * Determine platform type
 */
const getPlatformType = (): 'mobile' | 'web' | 'server' => {
  if (platform.isReactNative) {
    return 'mobile';
  }
  if (platform.isBrowser) {
    return 'web';
  }
  return 'server';
};

/**
 * Determine OS type
 */
const getOsType = (): 'ios' | 'android' | 'other' => {
  if (platform.isIOS) {
    return 'ios';
  }
  if (platform.isAndroid) {
    return 'android';
  }
  return 'other';
};

/**
 * Get platform-specific configuration
 */
export const getPlatformConfig = () => ({
    platform: getPlatformType(),
    os: getOsType(),
    isMobile: platform.isMobile || platform.isReactNative,
  });

// Re-export all getters for convenience
export {
  // Environment
  getEnvironment,
  getEnvironmentConfig,
  isDevelopment,
  isProduction,
  isStaging,
  isDebugEnabled,
  // API
  getApiConfig,
  buildApiUrl,
  API_PATHS,
  // Features
  getFeatureFlags,
  isFeatureEnabled,
  createFeatureChecker,
  // Countries
  getCountryConfig,
  isCountrySupported,
  getSupportedCountryCodes,
  getCountryOptions,
  getSchoolTypes,
  getCurriculumTypes,
  getCurrencyConfig,
  getAddressFormat,
  getDateFormat,
  getPhoneFormat,
  getTimezone,
  DEFAULT_COUNTRY_CODE,
  // Google
  getGoogleConfig,
  getGoogleMapsConfig,
  getGoogleOAuthConfig,
  getGooglePlacesConfig,
  getMapStyles,
  // Components
  getComponentConfig,
};

// Re-export types
export type { EnvironmentConfig } from './environments';
export type { ApiConfig } from './api';
export type { SupportedCountryCode } from './countries';
export type { GoogleConfig } from './google';
export type { ComponentConfig } from './components';
